import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Grid,
  LinearProgress,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  HowToReg as RegisterIcon,
  Schedule as ScheduleIcon,
  Block as ClosedIcon,
  SportsSoccer as SoccerIcon,
  CheckCircle as CheckCircleIcon,
  SellOutlined as SoldOutIcon
} from '@mui/icons-material';
import unifiedApi from '../../utils/unifiedApi';
import DOMPurify from 'dompurify';

const POSITION_TYPE_LABELS = {
  OutfieldPlayer: 'Outfield Player',
  Goalkeeper: 'Goalkeeper',
  SpareOutfielder: 'Spare Outfielder',
  SpareGoalkeeper: 'Spare Goalkeeper'
};

// Returns true when rich-text HTML contains visible text or media,
// treating markup like "<p><br></p>" or whitespace-only content as blank.
const hasRichTextContent = (html) => {
  if (!html) return false;
  const withoutTags = html
    .replace(/<(img|br|hr|iframe|video|audio)\b[^>]*>/gi, ' ')
    .replace(/<[^>]*>/g, '');
  const text = withoutTags.replace(/&nbsp;/gi, ' ').replace(/\s+/g, ' ').trim();
  return text.length > 0 || /<(img|iframe|video|audio)\b/i.test(html);
};

const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const datePart = date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
  const timePart = date.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit'
  });
  return `${datePart} at ${timePart}`;
};

const formatCurrency = (cents) => {
  return `$${(cents / 100).toFixed(2)}`;
};

const POSITION_TYPE_ORDER = {
  OutfieldPlayer: 0,
  Goalkeeper: 1,
  SpareOutfielder: 2,
  SpareGoalkeeper: 3
};

const sortPositions = (positions) => {
  if (!positions) return [];
  return [...positions].sort((a, b) =>
    (POSITION_TYPE_ORDER[a.positionType] ?? 99) - (POSITION_TYPE_ORDER[b.positionType] ?? 99)
  );
};

const RegistrationStatusChip = ({ status }) => {
  const config = {
    Open: { color: 'success', icon: <RegisterIcon />, label: 'Open' },
    Upcoming: { color: 'info', icon: <ScheduleIcon />, label: 'Coming Soon' },
    Closed: { color: 'default', icon: <ClosedIcon />, label: 'Closed' }
  };

  const { color, icon, label } = config[status] || config.Closed;

  return (
    <Chip
      icon={icon}
      label={label}
      color={color}
      size="small"
      clickable={false}
      onClick={() => {}}
    />
  );
};

const PositionRow = ({ position }) => {
  const label = POSITION_TYPE_LABELS[position.positionType] || position.positionType;
  const total = position.totalSlots || 0;
  const filled = position.filledSlots || 0;
  const fillPercent = total > 0 ? Math.round((filled / total) * 100) : 0;

  return (
    <Box sx={{ mb: 1.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {label}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography
            variant="body2"
            sx={{ fontWeight: 700 }}
            color={position.soldOut ? 'error.main' : 'text.primary'}
          >
            {position.soldOut ? 'Sold Out' : `${filled}/${total}`}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {formatCurrency(position.priceInCents)}
          </Typography>
        </Box>
      </Box>
      <LinearProgress
        variant="determinate"
        value={fillPercent}
        color={position.soldOut ? 'error' : fillPercent >= 80 ? 'warning' : 'primary'}
        sx={{ height: 6, borderRadius: 3 }}
      />
    </Box>
  );
};

const LeagueRegistrationCard = ({ league, onRegister }) => {
  const isOpen = league.registrationStatus === 'Open';
  const isUpcoming = league.registrationStatus === 'Upcoming';
  const hasAvailableSpots = league.positions?.some(p => !p.soldOut);

  return (
    <Card
      elevation={2}
      sx={{
        height: '100%',
        minHeight: 320,
        minWidth: 280,
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: 4
        }
      }}
    >
      <CardContent sx={{ flexGrow: 1 }}>
        <Box sx={{ position: 'absolute', top: 12, right: 12 }}>
          <RegistrationStatusChip status={league.registrationStatus} />
        </Box>
        <Typography variant="h6" component="h2" sx={{ fontWeight: 600, mb: 2, pr: 10 }}>
          {league.leagueName}
        </Typography>

        {league.blurb && (
          <Box
            sx={{
              mb: 2,
              color: 'text.secondary',
              fontSize: '0.875rem',
              '& p': { mt: 0, mb: 1 },
              '& p:last-child': { mb: 0 },
              '& ul, & ol': { mt: 0, mb: 1, pl: 3 },
              '& a': { color: 'primary.main' },
            }}
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(league.blurb) }}
          />
        )}

        {isUpcoming && league.scheduledOpenDate && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Registration opens {formatDate(league.scheduledOpenDate)}
          </Alert>
        )}

        {(isOpen || isUpcoming) && league.positions?.length > 0 && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Available Positions
            </Typography>
            {sortPositions(league.positions).map((pos) => (
              <PositionRow key={pos.positionType} position={pos} />
            ))}
          </Box>
        )}
      </CardContent>

      <CardActions sx={{ px: 2, pb: 2 }}>
        {league.userRegistrationStatus === 'Paid' ? (
          <Button
            variant="outlined"
            fullWidth
            disabled
            startIcon={<CheckCircleIcon />}
          >
            Registered
          </Button>
        ) : isOpen && hasAvailableSpots ? (
          <Button
            variant="contained"
            color="primary"
            fullWidth
            startIcon={<RegisterIcon />}
            onClick={() => onRegister(league.leagueSlug)}
          >
            Register Now
          </Button>
        ) : isOpen && !hasAvailableSpots ? (
          <Button variant="outlined" disabled fullWidth>
            All Positions Filled
          </Button>
        ) : isUpcoming ? (
          <Button variant="outlined" disabled fullWidth startIcon={<ScheduleIcon />}>
            Opens {formatDate(league.scheduledOpenDate)}
          </Button>
        ) : (
          <Button variant="outlined" disabled fullWidth>
            Registration Closed
          </Button>
        )}
      </CardActions>
    </Card>
  );
};

const EventRegistrationStatusChip = ({ status, availableSlots }) => {
  const isSoldOut = status === 'Open' && availableSlots === 0;

  const config = {
    Open: { color: 'success', icon: <RegisterIcon />, label: 'Open' },
    Upcoming: { color: 'info', icon: <ScheduleIcon />, label: 'Coming Soon' },
    Closed: { color: 'default', icon: <ClosedIcon />, label: 'Closed' },
    SoldOut: { color: 'error', icon: <SoldOutIcon />, label: 'Sold Out' }
  };

  const key = isSoldOut ? 'SoldOut' : status;
  const { color, icon, label } = config[key] || config.Closed;

  return (
    <Chip
      icon={icon}
      label={label}
      color={color}
      size="small"
      clickable={false}
      onClick={() => {}}
    />
  );
};

const EventRegistrationCard = ({ event, onRegister }) => {
  const isOpen = event.registrationStatus === 'Open';
  const isUpcoming = event.registrationStatus === 'Upcoming';
  const isSoldOut = isOpen && event.availableSlots === 0;
  const summaryHtml = hasRichTextContent(event.excerpt) ? event.excerpt : event.description;
  const sanitizedSummary = summaryHtml ? DOMPurify.sanitize(summaryHtml) : '';

  return (
    <Card
      elevation={2}
      sx={{
        height: '100%',
        minHeight: 280,
        minWidth: 280,
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: 4
        }
      }}
    >
      <CardContent sx={{ flexGrow: 1 }}>
        <Box sx={{ position: 'absolute', top: 12, right: 12 }}>
          <EventRegistrationStatusChip status={event.registrationStatus} availableSlots={event.availableSlots} />
        </Box>
        <Typography variant="h6" component="h2" sx={{ fontWeight: 600, mb: 2, pr: 10 }}>
          {event.eventName}
        </Typography>

        {sanitizedSummary && (
          <Box
            sx={{
              mb: 2,
              color: 'text.secondary',
              fontSize: '0.875rem',
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
              '& p': { mt: 0, mb: 1 },
              '& p:last-child': { mb: 0 },
              '& ul, & ol': { mt: 0, mb: 1, pl: 3 },
              '& a': { color: 'primary.main' },
            }}
            dangerouslySetInnerHTML={{ __html: sanitizedSummary }}
          />
        )}

        {isUpcoming && event.scheduledOpenDate && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Registration opens {formatDate(event.scheduledOpenDate)}
          </Alert>
        )}

        {(isOpen || isUpcoming) && (() => {
          const slotFillRatio = event.totalSlots > 0 ? event.filledSlots / event.totalSlots : 0;
          const slotFillPercent = Math.round(slotFillRatio * 100);

          return (
            <Box sx={{ mt: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Spots
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 700 }}
                    color={isSoldOut ? 'error.main' : 'text.primary'}
                  >
                    {isSoldOut ? 'Sold Out' : `${event.filledSlots}/${event.totalSlots}`}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatCurrency(event.priceInCents)}
                  </Typography>
                </Box>
              </Box>
              <LinearProgress
                variant="determinate"
                value={slotFillPercent}
                color={isSoldOut ? 'error' : slotFillRatio >= 0.8 ? 'warning' : 'primary'}
                sx={{ height: 6, borderRadius: 3 }}
              />
            </Box>
          );
        })()}
      </CardContent>

      <CardActions sx={{ px: 2, pb: 2 }}>
        {event.userRegistrationStatus === 'Paid' ? (
          <Button
            variant="outlined"
            fullWidth
            disabled
            startIcon={<CheckCircleIcon />}
          >
            Registered
          </Button>
        ) : isOpen && !isSoldOut ? (
          <Button
            variant="contained"
            color="primary"
            fullWidth
            startIcon={<RegisterIcon />}
            onClick={() => onRegister(event.eventSlug)}
          >
            Register Now
          </Button>
        ) : isSoldOut ? (
          <Button variant="outlined" disabled fullWidth>
            Sold Out
          </Button>
        ) : isUpcoming ? (
          <Button variant="outlined" disabled fullWidth startIcon={<ScheduleIcon />}>
            Opens {formatDate(event.scheduledOpenDate)}
          </Button>
        ) : (
          <Button variant="outlined" disabled fullWidth>
            Registration Closed
          </Button>
        )}
      </CardActions>
    </Card>
  );
};

const RegistrationLandingPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [data, setData] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadLanding = async () => {
      try {
        setLoading(true);
        setError(null);
        const [landingResult, eventsResult] = await Promise.all([
          unifiedApi.getRegistrationLanding(),
          unifiedApi.getPublicEvents()
        ]);
        setData(landingResult);
        setEvents(eventsResult || []);
      } catch (err) {
        setError('Failed to load registration information.');
        console.error('Error loading registration landing:', err);
      } finally {
        setLoading(false);
      }
    };

    loadLanding();
  }, []);

  const handleRegister = (leagueSlug) => {
    navigate(`/leagues/${leagueSlug}/register`);
  };

  const handleEventRegister = (eventSlug) => {
    navigate(`/events/${eventSlug}/register`);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box mt={4}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const leagues = data?.leagues || [];
  const hasLeagues = leagues.length > 0;
  const hasEvents = events.length > 0;

  return (
    <Box>
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <SoccerIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
        <Typography variant={isMobile ? 'h5' : 'h4'} component="h1" sx={{ fontWeight: 700 }}>
          Registration
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
          Find and register for an upcoming league or event
        </Typography>
      </Box>

      {!hasLeagues && !hasEvents && (
        <Alert severity="info" sx={{ mt: 2 }}>
          There are no leagues or events with registration available at this time.
        </Alert>
      )}

      {hasLeagues && (
        <>
          {hasEvents && (
            <Typography variant="h5" component="h2" sx={{ fontWeight: 600, mb: 2 }}>
              Leagues
            </Typography>
          )}
          <Grid container spacing={3} justifyContent="center">
            {leagues.map((league) => (
              <Grid item xs={12} sm={6} md={4} key={league.leagueId}>
                <LeagueRegistrationCard league={league} onRegister={handleRegister} />
              </Grid>
            ))}
          </Grid>
        </>
      )}

      {hasEvents && (
        <Box sx={{ mt: hasLeagues ? 6 : 0 }}>
          <Typography variant="h5" component="h2" sx={{ fontWeight: 600, mb: 2 }}>
            Events
          </Typography>
          <Grid container spacing={3} justifyContent="center">
            {events.map((event) => (
              <Grid item xs={12} sm={6} md={4} key={event.eventId}>
                <EventRegistrationCard event={event} onRegister={handleEventRegister} />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
    </Box>
  );
};

export default RegistrationLandingPage;
